import {useCallback, useEffect, useMemo, useReducer, useRef} from "react";
import * as Crypto from "expo-crypto";
import {PaymentType} from "../../types";
import ServiceClient from "../../utils/ServiceClient";
import {CardDestinationView, WithdrawReceiptSnapshot} from "./types";
import {createPayoutVerificationState, payoutVerificationReducer} from "./payoutVerificationState";
import {usePayoutResendCooldown} from "./usePayoutResendCooldown";
import {
  buildPayoffV2Payload, buildSafeWithdrawFlowErrorLog, buildVerifiedPayoffV2Payload, buildWithdrawalIntentSignature,
  buildWithdrawResultFromPayoff, createWithdrawalAttempt, getPayoffErrorCode, getPayoffErrorMessage,
  getPayoffVerificationErrorMessage, isPayoffVerificationError, isSameWithdrawalIntentSignature,
  isTerminalPayoffVerificationError, resolvePayoffReceiptSnapshot, WithdrawalAttempt, WithdrawalIntentSignature
} from "./utils";
import {
  isWithdrawalAttemptStoreCorruptionError, isWithdrawalAttemptStoreError, loadWithdrawalAttempt,
  removeWithdrawalAttempt, restoreWithdrawalAttemptAsReady, saveWithdrawalAttempt
} from "./withdrawalAttemptStore";

type Params = {
  isActive: boolean, userId: string, amountCents: number, methodType?: PaymentType,
  selectedDestination?: CardDestinationView, currentReceiptSnapshot?: WithdrawReceiptSnapshot,
  refreshUser: () => Promise<unknown>
};

type OtpOperation = { kind: 'resend' | 'submit', intentVersion: number };
type LatestWithdrawalIntent = { userId: string, signature?: WithdrawalIntentSignature };

const withdrawalAttemptUnavailableMessage = 'We could not safely prepare this withdrawal. Please try again.';
const withdrawalAttemptCorruptionMessage = 'We could not safely verify a previous withdrawal. Check Transaction History or contact support before trying again.';
const uncertainWithdrawalMessage = 'We could not confirm whether this withdrawal was completed.';

const buildSafeAttemptStoreErrorLog = (error: unknown) => isWithdrawalAttemptStoreError(error) ? {
  operation: error.operation, reason: error.reason
} : {operation: 'unknown', reason: 'unknown'};

export const usePayoutVerification = (params: Params) => {
  const {isActive, userId, amountCents, methodType, selectedDestination, currentReceiptSnapshot, refreshUser} = params;
  const [state, dispatch] = useReducer(payoutVerificationReducer, createPayoutVerificationState());
  const {
    secondsRemaining: resendAvailableInSeconds, startCooldown: startResendCooldown, resetCooldown: resetResendCooldown
  } = usePayoutResendCooldown();
  const withdrawalAttemptRef = useRef<WithdrawalAttempt | undefined>(undefined);
  const withdrawalIntentSignatureRef = useRef<WithdrawalIntentSignature | undefined>(undefined);
  const withdrawalAttemptUserIdRef = useRef('');
  const currentUserIdRef = useRef(userId);
  const withdrawalIntentVersionRef = useRef(0);
  const startingVerificationVersionRef = useRef<number | undefined>(undefined);
  const otpOperationRef = useRef<OtpOperation | undefined>(undefined);
  const terminalAttemptRetirementRequiredRef = useRef(false);
  const latestWithdrawalIntentRef = useRef<LatestWithdrawalIntent>({userId: ''});

  const currentIntentSignature = useMemo(() => {
    if (!isActive || !userId || !methodType) return undefined;
    if (methodType === 'CARD' && !selectedDestination?.id) return undefined;
    return buildWithdrawalIntentSignature(amountCents, methodType, selectedDestination?.id);
  }, [amountCents, isActive, methodType, selectedDestination?.id, userId]);
  latestWithdrawalIntentRef.current = {userId, signature: currentIntentSignature};

  const getCurrentIntentSignature = useCallback(() => currentIntentSignature, [currentIntentSignature]);
  const isCurrentWithdrawalIntent = useCallback((expectedUserId: string, expectedSignature: WithdrawalIntentSignature, intentVersion: number) => {
    const latestIntent = latestWithdrawalIntentRef.current;
    return intentVersion === withdrawalIntentVersionRef.current && latestIntent.userId === expectedUserId && !!latestIntent.signature && isSameWithdrawalIntentSignature(latestIntent.signature, expectedSignature);
  }, []);

  const resetPayoutVerification = useCallback(() => {
    withdrawalIntentVersionRef.current += 1;
    resetResendCooldown();
    dispatch({scope: 'flow', type: 'RESET_VERIFICATION'});
  }, [resetResendCooldown]);

  const resetPayoutFlow = useCallback(() => {
    withdrawalIntentVersionRef.current += 1;
    withdrawalAttemptRef.current = undefined;
    withdrawalIntentSignatureRef.current = undefined;
    withdrawalAttemptUserIdRef.current = '';
    terminalAttemptRetirementRequiredRef.current = false;
    resetResendCooldown();
    dispatch({scope: 'flow', type: 'RESET_FLOW'});
  }, [resetResendCooldown]);

  const replaceWithdrawalIntent = useCallback((signature?: WithdrawalIntentSignature) => {
    const currentSignature = withdrawalIntentSignatureRef.current;
    const userChanged = withdrawalAttemptUserIdRef.current !== userId;
    const signatureUnchanged = (!currentSignature && !signature) || !!(currentSignature && signature && isSameWithdrawalIntentSignature(currentSignature, signature));
    if (!userChanged && signatureUnchanged) return;

    withdrawalAttemptUserIdRef.current = userId;
    withdrawalIntentSignatureRef.current = signature;
    withdrawalAttemptRef.current = undefined;
    terminalAttemptRetirementRequiredRef.current = false;
    resetPayoutVerification();
  }, [resetPayoutVerification, userId]);

  currentUserIdRef.current = userId;

  const resolveCurrentWithdrawalAttempt = useCallback(async (signature: WithdrawalIntentSignature, intentVersion: number) => {
    const currentAttempt = withdrawalAttemptRef.current;
    if (withdrawalAttemptUserIdRef.current === userId && currentAttempt && isSameWithdrawalIntentSignature(currentAttempt.signature, signature)) return currentAttempt;

    const nextAttempt = await loadWithdrawalAttempt(userId, signature) || createWithdrawalAttempt(signature, Crypto.randomUUID());
    if (currentUserIdRef.current === userId && intentVersion === withdrawalIntentVersionRef.current) {
      withdrawalAttemptUserIdRef.current = userId;
      withdrawalIntentSignatureRef.current = signature;
      withdrawalAttemptRef.current = nextAttempt;
    }
    return nextAttempt;
  }, [userId]);

  const retireCurrentWithdrawalAttempt = useCallback(async (signature: WithdrawalIntentSignature, intentVersion: number) => {
    const currentAttempt = withdrawalAttemptRef.current;
    if (withdrawalAttemptUserIdRef.current === userId && currentAttempt &&
      isSameWithdrawalIntentSignature(currentAttempt.signature, signature)) {
      await removeWithdrawalAttempt(userId, currentAttempt);
    }

    if (currentUserIdRef.current !== userId || intentVersion !== withdrawalIntentVersionRef.current) return false;

    withdrawalAttemptRef.current = undefined;
    terminalAttemptRetirementRequiredRef.current = false;
    return true;
  }, [userId]);

  const restoreRetryableWithdrawalAttempt = useCallback(async (attempt: WithdrawalAttempt, signature: WithdrawalIntentSignature, intentVersion: number) => {
    const retryableAttempt: WithdrawalAttempt = {...attempt, status: 'ready'};
    await restoreWithdrawalAttemptAsReady(userId, attempt);
    if (!isCurrentWithdrawalIntent(userId, signature, intentVersion)) return false;

    withdrawalAttemptRef.current = retryableAttempt;
    return true;
  }, [isCurrentWithdrawalIntent, userId]);

  const markCurrentWithdrawalAttemptUncertain = useCallback(async (signature: WithdrawalIntentSignature, intentVersion: number): Promise<WithdrawalAttempt | undefined> => {
    const currentAttempt = withdrawalAttemptRef.current;
    if (withdrawalAttemptUserIdRef.current !== userId || !currentAttempt || !isSameWithdrawalIntentSignature(currentAttempt.signature, signature)) return undefined;

    const uncertainAttempt: WithdrawalAttempt = {...currentAttempt, status: 'uncertain'};
    await saveWithdrawalAttempt(userId, uncertainAttempt);
    if (isCurrentWithdrawalIntent(userId, signature, intentVersion)) {
      withdrawalAttemptRef.current = uncertainAttempt;
    }
    return currentAttempt;
  }, [isCurrentWithdrawalIntent, userId]);

  const showUncertainWithdrawalResult = useCallback(() => dispatch({
    scope: 'submission', type: 'SUBMISSION_RESULT',
    result: {kind: 'uncertain', message: uncertainWithdrawalMessage}
  }), []);

  const showWithdrawalAttemptCorruption = useCallback((error: unknown) => {
    if (!isWithdrawalAttemptStoreCorruptionError(error)) return false;

    dispatch({
      scope: 'submission', type: 'SUBMISSION_RESULT',
      result: {kind: 'uncertain', message: withdrawalAttemptCorruptionMessage}
    });
    return true;
  }, []);

  useEffect(() => {
    replaceWithdrawalIntent(getCurrentIntentSignature());
  }, [getCurrentIntentSignature, replaceWithdrawalIntent]);

  useEffect(() => () => {
    withdrawalIntentVersionRef.current += 1;
  }, []);

  const requestPayoutVerification = useCallback(async (forceNewAttempt: boolean) => {
    const activeIntentVersion = withdrawalIntentVersionRef.current;
    if (state.isStartingVerification || startingVerificationVersionRef.current === activeIntentVersion || !methodType || !currentReceiptSnapshot) {
      return false;
    }

    const signature = getCurrentIntentSignature();
    if (!signature) return false;
    if (!forceNewAttempt && !terminalAttemptRetirementRequiredRef.current && state.payoutVerification?.verificationId && state.verificationIntentSignature && isSameWithdrawalIntentSignature(state.verificationIntentSignature, signature)) {
      dispatch({scope: 'verification', type: 'USE_EXISTING_VERIFICATION', receiptSnapshot: currentReceiptSnapshot});
      return true;
    }

    const intentVersion = withdrawalIntentVersionRef.current;
    startingVerificationVersionRef.current = intentVersion;
    dispatch({scope: 'verification', type: 'START_VERIFICATION', receiptSnapshot: currentReceiptSnapshot});

    try {
      if (forceNewAttempt || terminalAttemptRetirementRequiredRef.current) {
        const retired = await retireCurrentWithdrawalAttempt(signature, intentVersion);
        if (!retired) return false;
      }

      const currentAttempt = await resolveCurrentWithdrawalAttempt(signature, intentVersion);
      if (!isCurrentWithdrawalIntent(userId, signature, intentVersion)) return false;
      if (currentAttempt.status === 'uncertain') {
        dispatch({
          scope: 'submission', type: 'SUBMISSION_RESULT',
          result: {kind: 'uncertain', message: uncertainWithdrawalMessage}
        });
        return false;
      }

      await saveWithdrawalAttempt(userId, currentAttempt);
      if (!isCurrentWithdrawalIntent(userId, signature, intentVersion)) return false;

      const payGateToken = await ServiceClient.getPayGateToken();
      if (!isCurrentWithdrawalIntent(userId, signature, intentVersion)) return false;
      const verification = await ServiceClient.startPayoffVerification(buildPayoffV2Payload(amountCents, methodType, selectedDestination), currentAttempt.idempotencyKey, payGateToken);
      if (!isCurrentWithdrawalIntent(userId, signature, intentVersion)) return false;

      startResendCooldown(verification.resendAvailableInSeconds);
      dispatch({scope: 'verification', type: 'VERIFICATION_STARTED', verification, signature});
      return true;

    } catch (err) {
      if (!isCurrentWithdrawalIntent(userId, signature, intentVersion)) return false;

      console.log('startPayoffVerification error', buildSafeWithdrawFlowErrorLog(err));
      if (showWithdrawalAttemptCorruption(err)) return false;
      dispatch({
        scope: 'verification', type: 'VERIFICATION_START_ERROR',
        message: isWithdrawalAttemptStoreError(err) ? withdrawalAttemptUnavailableMessage : isPayoffVerificationError(err) ? getPayoffVerificationErrorMessage(err) : getPayoffErrorMessage(err)
      });
      return false;

    } finally {
      if (startingVerificationVersionRef.current === intentVersion) {
        startingVerificationVersionRef.current = undefined;
      }
      if (isCurrentWithdrawalIntent(userId, signature, intentVersion)) {
        dispatch({scope: 'verification', type: 'VERIFICATION_START_FINISHED'});
      }
    }
  }, [
    amountCents, currentReceiptSnapshot, getCurrentIntentSignature, isCurrentWithdrawalIntent, methodType,
    resolveCurrentWithdrawalAttempt, retireCurrentWithdrawalAttempt, selectedDestination, state.isStartingVerification,
    state.payoutVerification?.verificationId, state.verificationIntentSignature, showWithdrawalAttemptCorruption,
    startResendCooldown, userId
  ]);

  const startPayoutVerification = useCallback(() => requestPayoutVerification(false), [requestPayoutVerification]);
  const restartPayoutVerification = useCallback(() => requestPayoutVerification(true), [requestPayoutVerification]);

  const handleTerminalVerificationError = useCallback(async (err: unknown, signature: WithdrawalIntentSignature, intentVersion: number) => {
    terminalAttemptRetirementRequiredRef.current = true;
    resetResendCooldown();
    dispatch({
      scope: 'verification', type: 'TERMINAL_VERIFICATION_ERROR', message: getPayoffVerificationErrorMessage(err)
    });

    try {
      await retireCurrentWithdrawalAttempt(signature, intentVersion);

    } catch (storageError) {
      if (!isCurrentWithdrawalIntent(userId, signature, intentVersion)) return;

      console.log('retire terminal withdrawal attempt error', buildSafeAttemptStoreErrorLog(storageError));
      if (showWithdrawalAttemptCorruption(storageError)) return;
      dispatch({
        scope: 'verification', type: 'TERMINAL_VERIFICATION_ERROR', message: withdrawalAttemptUnavailableMessage
      });
    }
  }, [
    isCurrentWithdrawalIntent, resetResendCooldown, retireCurrentWithdrawalAttempt, showWithdrawalAttemptCorruption,
    userId
  ]);

  const resendPayoutVerification = useCallback(async () => {
    const activeIntentVersion = withdrawalIntentVersionRef.current;
    if (otpOperationRef.current?.intentVersion === activeIntentVersion || !state.payoutVerification?.verificationId || state.isResendingVerification || state.isSubmittingWithdrawal || resendAvailableInSeconds > 0) return;

    const signature = getCurrentIntentSignature();
    if (!signature || !state.verificationIntentSignature || !isSameWithdrawalIntentSignature(state.verificationIntentSignature, signature)) {
      terminalAttemptRetirementRequiredRef.current = true;
      dispatch({
        scope: 'verification', type: 'TERMINAL_VERIFICATION_ERROR', message: 'Please request a new verification code.'
      });
      return;
    }

    const intentVersion = withdrawalIntentVersionRef.current;
    const operation: OtpOperation = {kind: 'resend', intentVersion};
    otpOperationRef.current = operation;
    dispatch({scope: 'resend', type: 'RESEND_STARTED'});
    let retryableAttempt: WithdrawalAttempt | undefined;

    try {
      retryableAttempt = await markCurrentWithdrawalAttemptUncertain(signature, intentVersion);

      if (!retryableAttempt) {
        if (isCurrentWithdrawalIntent(userId, signature, intentVersion)) showUncertainWithdrawalResult();
        return;
      }

      if (!isCurrentWithdrawalIntent(userId, signature, intentVersion)) {
        await restoreRetryableWithdrawalAttempt(retryableAttempt, signature, intentVersion);
        return;
      }

      const payGateToken = await ServiceClient.getPayGateToken();
      if (!isCurrentWithdrawalIntent(userId, signature, intentVersion)) {
        await restoreRetryableWithdrawalAttempt(retryableAttempt, signature, intentVersion);
        return;
      }
      const resend = await ServiceClient.resendPayoffVerification(state.payoutVerification.verificationId, payGateToken);

      const restored = await restoreRetryableWithdrawalAttempt(retryableAttempt, signature, intentVersion);
      if (!restored) return;

      startResendCooldown(resend.resendAvailableInSeconds);

    } catch (err) {
      const isCurrentIntent = () => isCurrentWithdrawalIntent(userId, signature, intentVersion);

      console.log('resendPayoffVerification error', buildSafeWithdrawFlowErrorLog(err));
      if (isWithdrawalAttemptStoreError(err)) {
        console.log('prepare resend withdrawal attempt error', buildSafeAttemptStoreErrorLog(err));

        if (isCurrentIntent()) {
          if (showWithdrawalAttemptCorruption(err)) return;
          dispatch({scope: 'resend', type: 'RESEND_ERROR', message: withdrawalAttemptUnavailableMessage});
        }
        return;
      }

      if (getPayoffErrorCode(err) === 'OTP_VERIFICATION_NOT_FOUND') {
        if (isCurrentIntent()) showUncertainWithdrawalResult();
        return;
      }

      if (isTerminalPayoffVerificationError(err)) {
        if (isCurrentIntent()) {
          await handleTerminalVerificationError(err, signature, intentVersion);

        } else if (retryableAttempt) {
          try {
            await removeWithdrawalAttempt(userId, retryableAttempt);

          } catch (storageError) {
            console.log('retire stale resend withdrawal attempt error', buildSafeAttemptStoreErrorLog(storageError));
          }
        }
        return;
      }

      if (retryableAttempt) {
        try {
          await restoreRetryableWithdrawalAttempt(retryableAttempt, signature, intentVersion);

        } catch (storageError) {
          console.log('restore resend withdrawal attempt error', buildSafeAttemptStoreErrorLog(storageError));

          if (isCurrentIntent()) {
            if (showWithdrawalAttemptCorruption(storageError)) return;
            dispatch({scope: 'resend', type: 'RESEND_ERROR', message: withdrawalAttemptUnavailableMessage});
          }
          return;
        }
      }

      if (isCurrentIntent()) {
        dispatch({
          scope: 'resend', type: 'RESEND_ERROR',
          message: isPayoffVerificationError(err) ? getPayoffVerificationErrorMessage(err) : getPayoffErrorMessage(err)
        });
      }

    } finally {
      if (otpOperationRef.current === operation) otpOperationRef.current = undefined;
      if (isCurrentWithdrawalIntent(userId, signature, intentVersion)) {
        dispatch({scope: 'resend', type: 'RESEND_FINISHED'});
      }
    }
  }, [
    getCurrentIntentSignature, handleTerminalVerificationError, isCurrentWithdrawalIntent,
    markCurrentWithdrawalAttemptUncertain, restoreRetryableWithdrawalAttempt, showUncertainWithdrawalResult,
    showWithdrawalAttemptCorruption, state.isResendingVerification, state.isSubmittingWithdrawal,
    state.payoutVerification?.verificationId, state.verificationIntentSignature, resendAvailableInSeconds,
    startResendCooldown, userId
  ]);

  const submitWithdrawal = useCallback(async (): Promise<'handled' | 'method-required'> => {
    const activeIntentVersion = withdrawalIntentVersionRef.current;
    if (otpOperationRef.current?.intentVersion === activeIntentVersion || state.isSubmittingWithdrawal || state.isResendingVerification) return 'handled';
    if (!methodType) return 'method-required';

    const signature = getCurrentIntentSignature();
    if (!signature) return 'method-required';
    if (!state.payoutVerification?.verificationId || !state.verificationIntentSignature || !isSameWithdrawalIntentSignature(state.verificationIntentSignature, signature)) {
      terminalAttemptRetirementRequiredRef.current = true;
      dispatch({
        scope: 'verification', type: 'TERMINAL_VERIFICATION_ERROR', message: 'Please request a new verification code.'
      });
      return 'handled';
    }

    const normalizedOtpCode = state.otpCode.replace(/\D/g, '').slice(0, 6);
    if (normalizedOtpCode.length !== 6) {
      dispatch({scope: 'verification', type: 'SET_OTP_ERROR', message: 'Enter the 6-digit verification code.'});
      return 'handled';
    }

    const currentAttempt = withdrawalAttemptRef.current;
    if (withdrawalAttemptUserIdRef.current !== userId || !currentAttempt || !isSameWithdrawalIntentSignature(currentAttempt.signature, signature)) {
      terminalAttemptRetirementRequiredRef.current = true;
      dispatch({
        scope: 'verification', type: 'TERMINAL_VERIFICATION_ERROR', message: 'Please request a new verification code.'
      });
      return 'handled';
    }
    if (currentAttempt.status === 'uncertain') {
      dispatch({
        scope: 'submission', type: 'SUBMISSION_RESULT', result: {kind: 'uncertain', message: uncertainWithdrawalMessage}
      });
      return 'handled';
    }

    const intentVersion = withdrawalIntentVersionRef.current;
    const operation: OtpOperation = {kind: 'submit', intentVersion};
    otpOperationRef.current = operation;
    dispatch({scope: 'submission', type: 'SUBMISSION_STARTED'});
    let payoffRequestStarted = false;

    try {
      const payGateToken = await ServiceClient.getPayGateToken();
      if (!isCurrentWithdrawalIntent(userId, signature, intentVersion)) return 'handled';

      const uncertainAttempt: WithdrawalAttempt = {...currentAttempt, status: 'uncertain'};
      await saveWithdrawalAttempt(userId, uncertainAttempt);
      if (!isCurrentWithdrawalIntent(userId, signature, intentVersion)) {
        await restoreRetryableWithdrawalAttempt(currentAttempt, signature, intentVersion);
        return 'handled';
      }
      withdrawalAttemptRef.current = uncertainAttempt;

      payoffRequestStarted = true;
      const payoff = await ServiceClient.payoffV2(buildVerifiedPayoffV2Payload(amountCents, methodType, selectedDestination, {
        verificationId: state.payoutVerification.verificationId, otpCode: normalizedOtpCode
      }), currentAttempt.idempotencyKey, payGateToken);

      try {
        await removeWithdrawalAttempt(userId, currentAttempt);

      } catch (storageError) {
        console.log('remove withdrawal attempt error', buildSafeAttemptStoreErrorLog(storageError));
      }

      if (isCurrentWithdrawalIntent(userId, signature, intentVersion)) {
        const quotedReceiptSnapshot = state.receiptSnapshot || currentReceiptSnapshot;
        const receiptResolution = quotedReceiptSnapshot ? resolvePayoffReceiptSnapshot(payoff, quotedReceiptSnapshot, amountCents) : undefined;
        if (receiptResolution?.source === 'invalid-response') {
          console.log('payoff response amount contract error', {
            status: payoff.status, amountPresent: payoff.amountCents != null, feePresent: payoff.feeAmountCents != null,
            netPresent: payoff.netAmountCents != null
          });
        }

        dispatch({
          scope: 'submission', type: 'SUBMISSION_RESULT',
          result: {...buildWithdrawResultFromPayoff(payoff), receiptSnapshot: receiptResolution?.snapshot}
        });
      }
      refreshUser().catch(err => {
        console.log('post-withdraw getUser error', buildSafeWithdrawFlowErrorLog(err));
      });

    } catch (err) {
      if (!isCurrentWithdrawalIntent(userId, signature, intentVersion)) return 'handled';

      console.log('payoffV2 error', buildSafeWithdrawFlowErrorLog(err));
      if (isWithdrawalAttemptStoreError(err)) {
        if (showWithdrawalAttemptCorruption(err)) return 'handled';
        dispatch({scope: 'verification', type: 'SET_OTP_ERROR', message: withdrawalAttemptUnavailableMessage});
        return 'handled';
      }
      if (isPayoffVerificationError(err)) {
        if (isTerminalPayoffVerificationError(err)) {
          await handleTerminalVerificationError(err, signature, intentVersion);
          return 'handled';
        }

        try {
          const restored = await restoreRetryableWithdrawalAttempt(currentAttempt, signature, intentVersion);
          if (!restored) return 'handled';

        } catch (storageError) {
          if (!isCurrentWithdrawalIntent(userId, signature, intentVersion)) return 'handled';
          console.log('restore retryable withdrawal attempt error', buildSafeAttemptStoreErrorLog(storageError));
          if (showWithdrawalAttemptCorruption(storageError)) return 'handled';
          dispatch({scope: 'verification', type: 'SET_OTP_ERROR', message: withdrawalAttemptUnavailableMessage});
          return 'handled';
        }

        dispatch({
          scope: 'verification', type: 'SET_OTP_ERROR', message: getPayoffVerificationErrorMessage(err)
        });
        return 'handled';
      }

      if (payoffRequestStarted) {
        dispatch({
          scope: 'submission', type: 'SUBMISSION_RESULT',
          result: {kind: 'uncertain', message: uncertainWithdrawalMessage}
        });
        return 'handled';
      }

      dispatch({
        scope: 'submission', type: 'SUBMISSION_RESULT', result: {kind: 'error', message: getPayoffErrorMessage(err)}
      });

    } finally {
      if (otpOperationRef.current === operation) otpOperationRef.current = undefined;
      if (isCurrentWithdrawalIntent(userId, signature, intentVersion)) {
        dispatch({scope: 'submission', type: 'SUBMISSION_FINISHED'});
      }
    }

    return 'handled';
  }, [
    amountCents, currentReceiptSnapshot, getCurrentIntentSignature, handleTerminalVerificationError,
    isCurrentWithdrawalIntent, methodType, refreshUser, restoreRetryableWithdrawalAttempt, selectedDestination,
    state.isResendingVerification, state.receiptSnapshot, state.isSubmittingWithdrawal, state.otpCode,
    state.payoutVerification?.verificationId, state.verificationIntentSignature, showWithdrawalAttemptCorruption, userId
  ]);

  const setOtpCode = useCallback((value: string) => {
    dispatch({scope: 'verification', type: 'SET_OTP_CODE', value});
  }, []);

  const clearOtpError = useCallback(() => dispatch({scope: 'verification', type: 'CLEAR_OTP_ERROR'}), []);
  const clearWithdrawResult = useCallback(() => dispatch({scope: 'flow', type: 'CLEAR_RESULT'}), []);

  return {
    ...state, resendAvailableInSeconds, setOtpCode, clearOtpError, clearWithdrawResult, resetPayoutVerification,
    resetPayoutFlow, startPayoutVerification, restartPayoutVerification, resendPayoutVerification, submitWithdrawal
  };
};
